import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Checkbox, Text, Icon, Tag, Tooltip, useDisclosure, Popover } from '@televet/kibble-ui';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  MergeProfilesPhoneNumber,
  updateMergeProfilesPhoneNumbers,
} from 'shared/components/MergeProfiles/state/mergeProfilesSlice';
import { formatPhoneNumber } from 'shared/utils';
import { IntegrationSystemDisplayNameType } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { MergeProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IMergeProfilesEditPhoneNumberProps {
  number: MergeProfilesPhoneNumber;
  isMergeResult: boolean;
  isPrimaryProfile: boolean;
  pimsName: IntegrationSystemDisplayNameType | null;
  disabled?: boolean;
  primaryDisabled: boolean;
}

const MergeProfilesPhoneNumberListItem = ({
  number,
  isMergeResult,
  isPrimaryProfile,
  pimsName,
  disabled,
  primaryDisabled,
}: IMergeProfilesEditPhoneNumberProps): JSX.Element => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const dispatch = useAppDispatch();
  const phoneNumbers = useAppSelector((state) => state.mergeProfiles.mergeProfileResult.phoneNumbers);
  const [newNumbers, setNewNumbers] = useState<MergeProfilesPhoneNumber[]>(phoneNumbers);
  const { trackEvent } = useTracking();

  useEffect(() => {
    setNewNumbers(phoneNumbers);
  }, [phoneNumbers]);

  const isPhoneNumberSelected = useCallback(
    (id: string | null) => !!phoneNumbers.some((phoneNumber) => phoneNumber.id === id && phoneNumber.isSelected),
    [phoneNumbers],
  );

  const handlePhoneNumberSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, selectedNumber: MergeProfilesPhoneNumber) => {
      const selected = !!e.currentTarget.checked;

      trackEvent(MergeProfileEvents.MergePhoneNumbers, { selected });

      const filteredPhoneNumbers = phoneNumbers.filter((number) => number.id !== selectedNumber.id);

      const newPhoneNumbers = [
        ...filteredPhoneNumbers,
        {
          ...selectedNumber,
          isPrimary: false,
          isSelected: selected,
        },
      ];

      dispatch(updateMergeProfilesPhoneNumbers(newPhoneNumbers));
    },
    [dispatch, phoneNumbers, trackEvent],
  );

  const setNumberPrimary = useCallback(
    (e) => {
      const selected = !!e.currentTarget.checked;

      trackEvent(MergeProfileEvents.SetPrimaryPhoneNumber, { selected });

      setNewNumbers((currentNumbers) => {
        return currentNumbers.map((selectedNumber) => {
          return {
            ...selectedNumber,
            isPrimary: selected && selectedNumber.id === number.id,
          };
        });
      });
    },
    [number.id, trackEvent],
  );

  const saveChanges = useCallback(() => {
    dispatch(updateMergeProfilesPhoneNumbers(newNumbers));
    onClose();
  }, [dispatch, newNumbers, onClose]);

  const PhoneNumberRow = (): JSX.Element => {
    return (
      <Flex key={number.id}>
        {isMergeResult ? (
          <Text size="sm">{formatPhoneNumber(number.number)}</Text>
        ) : (
          <Tooltip
            label={
              disabled ? `This option is locked because this profile is already in ${pimsName ?? 'your PIMS'}` : ''
            }
            titleCaseLabel={false}
          >
            <Box>
              <Checkbox
                defaultChecked={isPrimaryProfile}
                isChecked={isPhoneNumberSelected(number.id)}
                colorScheme="green"
                onChange={(e): void => handlePhoneNumberSelected(e, number)}
                isDisabled={!!disabled}
              >
                <Text size="sm">{formatPhoneNumber(number.number)}</Text>
              </Checkbox>
            </Box>
          </Tooltip>
        )}
        {(number.isPrimary || isMergeResult) && (
          <Tag
            label={number.isPrimary ? 'Primary' : ''}
            ml={2}
            backgroundColor="background.info"
            size="sm"
            leftElement={isMergeResult && !number.isPrimary ? <Icon size="sm" name="pen" /> : undefined}
            rightElement={isMergeResult && number.isPrimary ? <Icon size="sm" name="pen" /> : undefined}
            cursor={isMergeResult ? 'pointer' : 'default'}
            onClick={onToggle}
          />
        )}
      </Flex>
    );
  };

  return (
    <Popover
      size="sm"
      isOpen={isOpen}
      onClose={onClose}
      headerProps={{
        children: 'Edit Phone Number',
      }}
      footerProps={{
        onCancel: onClose,
        onSubmit: saveChanges,
        submitText: 'Save',
      }}
      placement="bottom"
      anchor={
        <Box alignSelf="flex-start">
          <PhoneNumberRow />
        </Box>
      }
    >
      {isOpen && (
        <>
          <Text variant="onContrast" size="sm" mb={2}>
            {formatPhoneNumber(number.number || '')}
          </Text>
          <Tooltip
            label={primaryDisabled ? 'This is not a mobile number and cannot be set as primary' : ''}
            titleCaseLabel={false}
          >
            <Box>
              <Checkbox defaultChecked={number.isPrimary} onChange={setNumberPrimary} isDisabled={primaryDisabled}>
                <Text variant="onContrast" size="md">
                  Primary
                </Text>
              </Checkbox>
            </Box>
          </Tooltip>
        </>
      )}
    </Popover>
  );
};

export default MergeProfilesPhoneNumberListItem;
